<template>
  <div class="bookshop">

    <!-- Header: first book's year (left) + topic filter (right) -->
    <div class="bookshop-header" v-if="filteredBooks.length">
      <span class="bookshop-year" v-if="filteredBooks[0].book.year">{{ filteredBooks[0].book.year }}</span>
      <select class="pill-select topic-filter" v-model="selectedTopic" v-if="allTopics.length > 1">
        <option value="">All Topics</option>
        <option v-for="t in allTopics" :key="t" :value="t">{{ t }}</option>
      </select>
    </div>
    <hr class="book-rule" v-if="filteredBooks.length" />

    <template v-for="(entry, index) in filteredBooks" :key="entry.book.uid">
      <!-- Year divider only between year changes (not before first book) -->
      <div class="year-divider" v-if="index > 0 && entry.book.year && entry.book.year !== filteredBooks[index - 1]?.book.year">
        <span class="bookshop-year">{{ entry.book.year }}</span>
        <hr class="book-rule" />
      </div>
      <div v-else-if="index > 0">
        <hr class="book-rule" />
      </div>

    <div class="bookshop-item">
      <div class="book-compact">

        <!-- Cover -->
        <div class="book-cover">
          <img v-if="entry.book.coverimages?.[0]?.url" :src="entry.book.coverimages[0].url" :alt="entry.book.title" />
          <div v-else class="cover-empty" />
        </div>

        <!-- Details -->
        <div class="book-details">
          <p class="book-title">{{ entry.book.title }}</p>
          <p class="book-price" v-if="priceOf(entry.book)">${{ priceOf(entry.book) }}</p>
          <div class="book-specs" v-if="entry.description || entry.book.specs">{{ entry.description || entry.book.specs }}</div>
          <div class="book-actions">
            <button class="add-btn" @click="addToCart(entry.book)">Add to Cart</button>
            <router-link class="about-btn" :to="'/' + entry.book.uid">Read more</router-link>
          </div>
        </div>

      </div>
    </div>
    </template>
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue'
  import { useStore } from '../KQL.js'
  import { pageFromPath } from '../helpers.js'
  import { useCartStore } from './CartStore.js'

  const kql = useStore();
  const cart = useCartStore();
  const props = defineProps(['block', 'page']);

  const selectedTopic = ref('');

  const books = computed(() => {
    return (props.block.content.rows || [])
      .map(row => {
        const uid = row.page?.[0];
        if (!uid) return null;
        const book = pageFromPath(uid, kql.pages);
        if (!book) return null;
        return { book, description: row.description || '' };
      })
      .filter(Boolean);
  });

  const topicsOf = (book) => {
    if (!book.topics) return [];
    if (Array.isArray(book.topics)) return book.topics;
    return book.topics.split(',').map(t => t.trim()).filter(Boolean);
  };

  const allTopics = computed(() => {
    const set = new Set();
    books.value.forEach(({ book }) => topicsOf(book).forEach(t => set.add(t)));
    return [...set].sort();
  });

  const filteredBooks = computed(() => {
    if (!selectedTopic.value) return books.value;
    return books.value.filter(({ book }) => topicsOf(book).includes(selectedTopic.value));
  });

  const priceOf = (book) => {
    const p = book?.price;  // book is the page object
    if (!p || parseFloat(p) === 0) return null;
    return parseFloat(p).toFixed(2);
  };

  const addToCart = async (book) => {
    const existing = cart.line_items.find(i => i._slug === book.uid);
    if (existing) {
      cart.updateQuantity(book.uid, existing.quantity + 1);
      cart.isOpen = true;
      cart.loadSession();
    } else {
      await cart.addItem(book);
    }
  };
</script>

<style scoped>
@import '../css/breakpoints.css';

  .bookshop {
    margin-top: 4rem;
  }

  .bookshop-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  .year-divider {
    margin-bottom: 2rem;
  }

  .year-divider .book-rule {
    margin-bottom: 1.5rem;
  }

  .bookshop-year {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: 1.8rem;
    color: #f1f2f2;
    display: block;
    margin-bottom: 1rem;
  }

  .bookshop-item {
    margin-bottom: 4rem;
  }

  .book-rule {
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.3);
    margin: 0 0 3.5rem;
  }

  .book-compact {
    display: flex;
    gap: 4rem;
    align-items: center;
  }

  /* Cover */
  .book-cover {
    flex-shrink: 0;
    width: 22rem;
    align-self: stretch;
    display: flex;
    align-items: flex-start;
  }

  .book-cover img {
    width: 100%;
    display: block;
    object-fit: contain;
    object-position: left top;
  }

  .cover-empty {
    width: 100%;
    aspect-ratio: 346 / 556;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 2px;
  }

  /* Details */
  .book-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 3.5rem;
    min-width: 0;
  }

  .book-title {
    font-family: "Copernicus", serif;
    font-size: 3rem;
    font-weight: normal;
    line-height: 1.2;
    color: #f1f2f2;
    max-width: 60rem;
    margin: 0;
  }

  .book-price {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: 1.8rem;
    color: #f1f2f2;
    margin: 0;
  }

  .book-specs {
    font-family: "Copernicus", serif;
    font-size: 1.8rem;
    line-height: 1.4;
    color: #f1f2f2;
    max-width: 80rem;
    opacity: 0.85;
  }

  .book-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .add-btn,
  .about-btn {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: 1.4rem;
    border-radius: 3em;
    padding: 0.65em 1.6em 0.5em;
    margin: 0;
    line-height: 1;
    display: inline-block;
    white-space: nowrap;
    transition: background 0.18s;
    cursor: pointer;
  }

  .add-btn {
    color: #895B5C;
    background: #f1f2f2;
    border: 1px solid #f1f2f2;
  }

  .add-btn:hover {
    background: white;
    border-color: white;
  }

  .about-btn {
    color: #f1f2f2;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.9);
  }

  .about-btn:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  .pill-select {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    color: #f1f2f2;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 3em;
    padding: 0.2em 2.5em 0.2em 0.9em;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4 3.5-4z' fill='white'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.8em center;
  }

  .pill-select:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }

  .pill-select option {
    color: #000;
    background: #fff;
  }

  /* Mobile */
  @media (--phone) {
    .book-compact {
      flex-direction: column;
      gap: 2.5rem;
      align-items: flex-start;
    }

    .book-cover {
      width: 100%;
      max-width: 22rem;
      align-self: auto;
    }

    .book-title {
      font-size: 2.4rem;
    }
  }
</style>
